import * as Header from '-/Custom/Header';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import Box from '@/components/UI/Box';
import Menu from '@/components/UI/Menu';
import Text from '@/components/UI/Text';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import Icon from '../../components/Unistyles/Icon';

export const EventDetailToolbarFragment = graphql(/* GraphQL */ `
  fragment EventDetailToolbar on event {
    id
    title
    event_type @_optional
    is_group @_optional
    permissions @_optional
    event @_optional {
      id
      title
    }
  }
`);

function EventDetailToolbar({
  query,
}: {
  query: FragmentOf<typeof EventDetailToolbarFragment> | null | undefined;
}) {
  const { eventId: id } = useLocalSearchParams<{
    eventId?: string;
  }>();
  const { navigate } = useRouter();

  const event = readFragment(EventDetailToolbarFragment, query);
  const showCreateButton = event?.is_group && event?.permissions?.create_event;

  const actions = {
    edit: event?.permissions?.edit,
  };

  const showEditButton = Object.values(actions).some(Boolean);

  const headerTitle = () => (
    <Header.TitleContainer>
      <Box flex justifyContent="center">
        <Text
          flexShrink={1}
          size="xl"
          numberOfLines={1}
          testID="ScreenHeaderPrimaryTitle"
        >
          {event?.title || 'Event'}
        </Text>
        {event?.event ? (
          <Box
            flexShrink={0}
            direction="horizontal"
            alignItems="center"
            space="sm"
          >
            <Icon
              variant="typographySecondary"
              name="trophy-outline"
              size="sm"
            />
            <Text variant="typographySecondary" size="xs">
              {event.event.title}
            </Text>
          </Box>
        ) : null}
      </Box>
    </Header.TitleContainer>
  );

  const headerRight = () =>
    showCreateButton || showEditButton ? (
      <Header.End>
        <Header.ButtonGroup>
          {showCreateButton ? (
            <Menu
              title="Create"
              actions={[
                {
                  id: 'generateSchedule',
                  title: 'Generate Schedule',
                  icon: 'flash-outline',
                  onSelect: () =>
                    navigate({
                      pathname: '/event/[eventId]/generate',
                      params: { eventId: id },
                    }),
                },
                {
                  id: 'createGroups',
                  title: 'League/Season',
                  icon: 'trophy-outline',
                  onSelect: () =>
                    navigate({
                      pathname: '/event/create',
                      params: { eventType: 'group', parentId: id },
                    }),
                },
                {
                  id: 'createGames',
                  title: 'Game',
                  icon: 'calendar-outline',
                  onSelect: () =>
                    navigate({
                      pathname: '/event/create',
                      params: { eventType: 'game', parentId: id },
                    }),
                },
              ]}
            >
              {(triggerProps) => (
                <Header.Button
                  {...triggerProps}
                  accessibilityLabel="Create event"
                  testID="EventCreateMenuButton"
                >
                  <Icon name="add" size="3xl" />
                </Header.Button>
              )}
            </Menu>
          ) : null}
          {showEditButton ? (
            <Header.Button
              accessibilityLabel="Edit event"
              testID="EventDetailEditButton"
              onPress={() => {
                navigate({
                  pathname: '/event/[eventId]/edit',
                  params: {
                    eventId: id,
                    parentId: event?.event?.id,
                  },
                });
              }}
            >
              <Icon name="settings-outline" size="2xl" />
            </Header.Button>
          ) : null}
        </Header.ButtonGroup>
      </Header.End>
    ) : null;

  return (
    <Stack.Screen
      options={{
        title: event?.title || '',
        headerRight,
        headerTitle,
      }}
    />
  );
}

export default EventDetailToolbar;
